// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import type { Fetcher, FetcherResults, ContextAwareFetcher } from '@ta/common.typeahead';

/** The fetcher ID */
export const ID = 'geonavi';

/**
 * A typeahead fetcher for GeoNavi.
 *
 * GeoNavi is a feature used in some APAC markets to allow users to navigate the geo hierarhcy with their mouse instead
 * of typing out names.
 */
class GeoNaviFetcher implements Fetcher, ContextAwareFetcher {
  id: string = ID;
  geoId: string = '1';
  limit: number = 26;
  cache: { [string]: FetcherResults | Promise<FetcherResults> } = {};
  results: FetcherResults = null;

  /** A stack of visited geo IDs that we capture as we drill down so we can navigate back up again */
  stack: Array<string> = [];

  constructor({ geoId, limit }: {| geoId?: string, limit?: number |}) {
    if (geoId) {
      this.geoId = geoId;
    }
    if (limit) {
      this.limit = limit;
    }
  }

  getResults(): FetcherResults {
    return this.results;
  }

  fetch(term: string, context: ?{ [string]: any }, onChange: () => mixed): void {
    this.onContextChange(context, onChange);
  }

  onContextChange(context: ?{ [string]: any }, onChange: () => mixed) {
    if (!context || !context.geoNaviId) {
      return;
    }
    const newGeoId = context.geoNaviId;

    // base case
    if (newGeoId === '1') {
      this.stack = [];

      // else if we are going back up the tree
    } else if (this.stack.indexOf(newGeoId) !== -1) {
      this.stack = this.stack.slice(0, this.stack.indexOf(newGeoId));

      // else stash the new geo id so we can walk back up the tree
    } else {
      this.stack.push(this.geoId);
    }

    this.geoId = newGeoId;

    // if a request for this geoId is already in flight then it's a no-op. The cache entry will be changed
    // from a Promise to the actual results when they are ready
    const cachedResult: FetcherResults | Promise<FetcherResults> = this.cache[this.geoId];
    if (cachedResult) {
      if (cachedResult instanceof Promise) {
        return;
      }

      // else else we have cached results so use them
      this.results = cachedResult;
      onChange();
      return;
    }

    const params = new URLSearchParams({
      action: 'GEONAVI',
      geoID: this.geoId,
    });

    const { geoId } = this;
    this.cache[geoId] = fetch(`/TypeAheadJson?${params.toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.ok) {
          return response;
        }
        throw Error(response.statusText);
      })
      .then(response => response.json())
      .then(results =>
        results.results.map((r, i) => {
          // a result with the same geo ID as the current scope is really a link back up the hierarhcy
          const resultId =
            String(r.id) === geoId ? this.stack[this.stack.length - 1] : String(r.id);
          const indicatesBack = i === 0 && parseInt(this.geoId, 10) !== 1; // We indicate back with an arrow if we've begun traversing the geonavi tree.
          return {
            name: r.displayName,
            geoId: resultId,
            back: indicatesBack,
            forward: !indicatesBack && !r.isLeaf,
          };
        }),
      )
      .then(data => {
        this.results = data.slice(0, this.limit);
        this.cache[geoId] = this.results;
        onChange();
        return this.results;
      })
      .catch(() => {
        this.results = [];
        onChange();
        return this.results;
      });
  }
}

export default GeoNaviFetcher;
